import React, {useContext, useEffect, useMemo} from "react";
import classnames from "classnames";
import UserPhoneHomepageTimeBasedTimer from "./timer";
import {UserHomepageControllerContext, UserHomepageStateContext, UserHomepageViewContext} from "../../../../../../index";
import {
    UserHomepageComponentTypes,
    UserHomepageControllerEvents,
    UserHomepageEventEmitter,
    UserHomepageViewMode
} from "../../../../../../models";
import UserHomepageUtils from "../../../../../../core/utils";
import {Fade, Tooltip} from "@mui/material";
import UserWebHomepageComponent from "../../index";

const UserWebHomepageTimeBased = ({component, tabId, bodyId}) => {
    const view = useContext(UserHomepageViewContext);
    const {viewMode} = useContext(UserHomepageStateContext);
    const controller = useContext(UserHomepageControllerContext);

    const productsComponent = useMemo(() => ({
        ...component,
        type: {
            id: UserHomepageComponentTypes.mediumProduct,
        },
    }), [component])

    /** @type{boolean} */
    const showMoreButton = useMemo(() =>
            (!!component.searchQuery && viewMode === UserHomepageViewMode.preview) ||
            (viewMode === UserHomepageViewMode.edit),
        [viewMode, component?.searchQuery])
    /** @type{boolean}*/
    const moreButtonInvisible = useMemo(() =>
            viewMode === UserHomepageViewMode.edit && !component.nexusSearchId,
        [viewMode, component?.nexusSearchId])

    /**
     * With each change in the controller:
     * - attaches the event listeners for component and content selection for focusing purposes.
     */
    useEffect(() => {
        controller.on(UserHomepageControllerEvents.onComponentSelected, focusComponent)
        return () => {
            controller.off(UserHomepageControllerEvents.onComponentSelected, focusComponent)
        }
    }, [controller, component, view, bodyId])

    /**
     * Scrolls the component into the view as a result of control panel selection of the component
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     * @param {number} componentId
     */
    const focusComponent = (emitter, tabId, componentId) => {
        if (componentId !== component.id)
            return;
        const body = document.getElementById(bodyId);
        body.scrollTo({
            top: document.getElementById(component.webViewElementId)?.offsetTop - body.offsetTop,
            left: 0,
        })
    }


    /**
     * Invokes the appropriate controller callback for the click of the time based more button.
     */
    const onMoreButtonClicked = () => {
        if (moreButtonInvisible) {
            return;
        }
        controller.selectComponentSearchButton(tabId, component.id, UserHomepageEventEmitter.view);
    }

    /**@type {JSX.Element}*/
    const moreButton = useMemo(() => (
        <button
            onClick={onMoreButtonClicked}
            className={classnames('user-web-homepage-more-button', {'not-visible': moreButtonInvisible})}
        >
            See All
        </button>
    ), [moreButtonInvisible])

    return (
        <>
            <div className={'user-web-homepage-time-based'}>
                <div className={'user-web-homepage-section user-web-homepage-time-based-header'}>
                    <div className={'component-title'}>
                        <p>
                            Deal Ends In
                        </p>
                    </div>
                    <UserPhoneHomepageTimeBasedTimer
                        endDate={UserHomepageUtils.getTimeBasedFinishDateTime(component)}
                    />
                    <Fade in={true}>
                        <div className={'d-flex justify-content-center w-100'}>
                            <Fade in={showMoreButton}>
                                {
                                    moreButtonInvisible
                                        ? <Tooltip
                                            title={'No search query or search type specified'}
                                        >
                                            {moreButton}
                                        </Tooltip>
                                        : moreButton
                                }
                            </Fade>
                        </div>
                    </Fade>
                </div>
                <UserWebHomepageComponent
                    component={productsComponent}
                    tabId={tabId}
                    bodyId={bodyId}
                    timeBased
                />
            </div>
        </>
    );
}


export default UserWebHomepageTimeBased;
